import React, { ReactElement, Fragment, useEffect, useState, useMemo } from "react";
import { gql, useQuery } from "urql";

import { DateHelper } from "../../../common/src";
import { useQueryState } from "../../../components/src/use-query-state";
import { Grid, Column } from "../../../components/src";
import { DateForm } from "../../../components/src/dateForm";

const getTimeseries = gql`
    query getTimeseries($filter: TimeSeriesFilterInput) {
        timeseries(includeItems: true, filter: $filter) {
            _id
            dates
            values
            status
            instrument {
                _id
                name
                currency
            }
        }
    }
`;

const getCdsIndexInstr = gql`
    query allInstrumentsInPosition($endDate: GraphQLDateString) {
        allInstrumentsInPosition(endDate: $endDate, modelTypes: CdsIndex) {
            instrument {
                _id
            }
        }
    }
`;

const getNavs = gql`
    query getNavs($date: GraphQLDateString!, $statusIn: [ReportStatusEnum!]) {
        navs(date: $date, statusIn: $statusIn) {
            _id
            date
            data {
                classes {
                    instrumentId
                    instrument {
                        longName
                    }
                    name
                    navPerUnit
                    navCurrency
                }
            }
        }
    }
`;

const getFxInstruments = gql`
    query getFxInstruments($nameIn: [String!]) {
        instruments(nameIn: $nameIn) {
            _id
            name
        }
    }
`;

type ValueItem = {
    name: string;
    value: string;
};

const getPreviousBusinessDay = (date: Date): Date => {
    const day = date.getDay(); // 0 = Sunday, 6 = Saturday
    if (day === 0) return DateHelper.addDays(date, -2); // Sunday → Friday
    if (day === 1) return DateHelper.addDays(date, -3); // Monday → Friday
    if (day === 6) return DateHelper.addDays(date, -1); // Saturday → Friday
    return DateHelper.addDays(date, -1); // Default: step back 1 day
};

export function MoversPage(): ReactElement {
    const [startDate] = useQueryState("startDate", getPreviousBusinessDay(new Date()).toISOString().slice(0, 10));
    const [endDate] = useQueryState("endDate", new Date().toISOString().slice(0, 10));

    const isWarningDateRange = new Date(startDate) > new Date(endDate);

    const [tsIrsData, setTsIrsData] = useState([]);
    const [tsMiscData, setTsMiscData] = useState([]);
    const moversReportStatuses = ["Active", "Locked"];

    const fxInstrumentNames = ["EURSEK", "USDSEK"];

    const [{ fetching: fetchingFxInstr, error: errorFxInstr, data: dataFxInstr }] = useQuery({
        query: getFxInstruments,
        variables: { nameIn: fxInstrumentNames },
        requestPolicy: "cache-and-network"
    });

    const instrumentIdsFx: string[] = useMemo(() => {
        let result: string[] = [];
        if (dataFxInstr) {
            result = dataFxInstr.instruments.map((inst) => inst._id);
        }
        return result;
    }, [dataFxInstr]);

    // Fixed curve of interest rate swaps
    // TODO: Use curve defs instead?
    const instrumentIrsIds = [
        "558bc96133d8650bd478e335",
        "558bc96133d8650bd478e351",
        "558bc96133d8650bd478e350",
        "558bc96133d8650bd478e353",
        "558bc96133d8650bd478e359",
        "558bc96133d8650bd478e35c"
    ];

    const [{ fetching: fetchingCdsInstr, error: errorCdsInstr, data: dataCdsInstr }] = useQuery({
        query: getCdsIndexInstr,
        variables: { endDate: endDate },
        requestPolicy: "cache-and-network"
    });

    const instrumentIdsCds: string[] = useMemo(() => {
        let result: string[] = [];
        if (dataCdsInstr) {
            result = dataCdsInstr.allInstrumentsInPosition.map((pos) => pos.instrument._id);
        }
        return result;
    }, [dataCdsInstr]);

    const [{ fetching: fetchingCdsStart, error: errorCdsStart, data: rawDataCdsStart }] = useQuery({
        query: getTimeseries,
        variables: instrumentIdsCds
            ? {
                  filter: {
                      instrumentIdIn: instrumentIdsCds,
                      typeIn: "Price(Close)",
                      startDate: startDate,
                      endDate: startDate
                  }
              }
            : undefined,
        pause: !instrumentIdsCds,
        requestPolicy: "cache-and-network"
    });

    const [{ fetching: fetchingCdsEnd, error: errorCdsEnd, data: rawDataCdsEnd }] = useQuery({
        query: getTimeseries,
        variables: instrumentIdsCds
            ? {
                  filter: {
                      instrumentIdIn: instrumentIdsCds,
                      typeIn: "Price(Close)",
                      startDate: endDate,
                      endDate: endDate
                  }
              }
            : undefined,
        pause: !instrumentIdsCds,
        requestPolicy: "cache-and-network"
    });

    const tsCdsData = useMemo(() => {
        let tsCdsData: { name: string; startValue: string | null; endValue: string | null }[] = [];
        if (rawDataCdsStart && rawDataCdsEnd) {
            const resultCdsStart: ValueItem[] = rawDataCdsStart.timeseries.map((inst) => ({
                name: inst.instrument.name,
                value: inst.values[0]
            }));
            const resultCdsEnd: ValueItem[] = rawDataCdsEnd.timeseries.map((inst) => ({
                name: inst.instrument.name,
                value: inst.values[0]
            }));

            const result: { name: string; startValue: string | null; endValue: string | null }[] = resultCdsStart.map((start) => {
                const end = resultCdsEnd.find((e) => e.name === start.name);

                return {
                    name: start.name,
                    startValue: (parseFloat(start.value) * 10000).toFixed(1),
                    endValue: (parseFloat(end?.value) * 10000).toFixed(1),
                    change: ((parseFloat(end?.value) - parseFloat(start.value)) * 10000).toFixed(1)
                };
            });
            tsCdsData = result;
        }
        return tsCdsData;
    }, [rawDataCdsStart, rawDataCdsEnd]);

    const [{ fetching: fetchingIrsStart, error: errorIrsStart, data: dataIrsStart }] = useQuery({
        query: getTimeseries,
        variables: {
            filter: {
                instrumentIdIn: instrumentIrsIds,
                typeIn: "Price(Last)",
                startDate: startDate,
                endDate: startDate
            }
        },
        requestPolicy: "cache-and-network"
    });

    const [{ fetching: fetchingIrsEnd, error: errorIrsEnd, data: dataIrsEnd }] = useQuery({
        query: getTimeseries,
        variables: {
            filter: {
                instrumentIdIn: instrumentIrsIds,
                typeIn: "Price(Last)",
                startDate: endDate,
                endDate: endDate
            }
        },
        requestPolicy: "cache-and-network"
    });

    const [{ fetching: fetchingNavsStart, error: errorNavsStart, data: dataNavsStart }] = useQuery({
        query: getNavs,
        variables: {
            date: startDate,
            statusIn: moversReportStatuses
        },
        requestPolicy: "cache-and-network"
    });

    const [{ fetching: fetchingNavsEnd, error: errorNavsEnd, data: dataNavsEnd }] = useQuery({
        query: getNavs,
        variables: {
            date: endDate,
            statusIn: moversReportStatuses
        },
        requestPolicy: "cache-and-network"
    });

    const [{ fetching: fetchingFxStart, error: errorFxStart, data: rawDataFxStart }] = useQuery({
        query: getTimeseries,
        variables: instrumentIdsFx.length > 0
            ? {
                  filter: {
                      instrumentIdIn: instrumentIdsFx,
                      typeIn: "Price(Close)",
                      startDate: startDate,
                      endDate: startDate
                  }
              }
            : undefined,
        pause: instrumentIdsFx.length === 0,
        requestPolicy: "cache-and-network"
    });

    const [{ fetching: fetchingFxEnd, error: errorFxEnd, data: rawDataFxEnd }] = useQuery({
        query: getTimeseries,
        variables: instrumentIdsFx.length > 0
            ? {
                  filter: {
                      instrumentIdIn: instrumentIdsFx,
                      typeIn: "Price(Close)",
                      startDate: endDate,
                      endDate: endDate
                  }
              }
            : undefined,
        pause: instrumentIdsFx.length === 0,
        requestPolicy: "cache-and-network"
    });

    const { fundNavsData } = useMemo(() => {
        let fundNavsData: { name: string; startValue: string; endValue: string; change: string }[] = [];
        if (dataNavsStart && dataNavsEnd && dataNavsStart.navs && dataNavsEnd.navs) {
            const resultNavsStart: Array<{ instrumentId: string; name: string; value: string }> = [];
            dataNavsStart.navs.forEach((nav) => {
                nav.data.classes.forEach((cls) => {
                    resultNavsStart.push({
                        instrumentId: cls.instrumentId,
                        name: cls.instrument?.longName || cls.name,
                        value: cls.navPerUnit
                    });
                });
            });

            const resultNavsEnd: Array<{ instrumentId: string; name: string; value: string }> = [];
            dataNavsEnd.navs.forEach((nav) => {
                nav.data.classes.forEach((cls) => {
                    resultNavsEnd.push({
                        instrumentId: cls.instrumentId,
                        name: cls.instrument?.longName || cls.name,
                        value: cls.navPerUnit
                    });
                });
            });

            const result: { name: string; startValue: string; endValue: string; change: string }[] = resultNavsStart.map((start) => {
                const end = resultNavsEnd.find((e) => e.instrumentId === start.instrumentId);

                const startValue = parseFloat(start.value);
                const endValue = end ? parseFloat(end.value) : null;
                let change = "";
                if (endValue !== null && !isNaN(startValue) && !isNaN(endValue) && startValue !== 0) {
                    change = (((endValue - startValue) / startValue) * 100).toFixed(2);
                }

                return {
                    name: start.name,
                    startValue: startValue.toFixed(4),
                    endValue: endValue !== null ? endValue.toFixed(4) : "",
                    change: change
                };
            });
            fundNavsData = result;
        }
        return { fundNavsData: fundNavsData };
    }, [dataNavsStart, dataNavsEnd]);

    const tsFxData = useMemo(() => {
        let tsFxData: { name: string; startValue: string; endValue: string; change: string }[] = [];
        if (rawDataFxStart && rawDataFxEnd) {
            const resultFxStart: ValueItem[] = rawDataFxStart.timeseries.map((inst) => ({
                name: inst.instrument.name,
                value: inst.values[0]
            }));
            const resultFxEnd: ValueItem[] = rawDataFxEnd.timeseries.map((inst) => ({
                name: inst.instrument.name,
                value: inst.values[0]
            }));

            const result: { name: string; startValue: string; endValue: string; change: string }[] = resultFxStart.map((start) => {
                const end = resultFxEnd.find((e) => e.name === start.name);

                const startValue = parseFloat(start.value);
                const endValue = end ? parseFloat(end.value) : null;
                let change = "";
                if (endValue !== null && !isNaN(startValue) && !isNaN(endValue) && startValue !== 0) {
                    change = (((endValue - startValue) / startValue) * 100).toFixed(2);
                }

                return {
                    name: start.name,
                    startValue: startValue.toFixed(4),
                    endValue: endValue !== null ? endValue.toFixed(4) : "",
                    change: change
                };
            });
            tsFxData = result;
        }
        return tsFxData;
    }, [rawDataFxStart, rawDataFxEnd]);

    useEffect(() => {
        if (dataIrsStart && dataIrsEnd) {
            const result_start: ValueItem[] = dataIrsStart.timeseries.map((inst) => ({
                name: inst.instrument.name,
                value: inst.values[0]
            }));
            const result_end: ValueItem[] = dataIrsEnd.timeseries.map((inst) => ({
                name: inst.instrument.name,
                value: inst.values[0]
            }));
            const result: { name: string; startValue: string | null; endValue: string | null; change: string | null }[] = result_start.map(
                (start) => {
                    const end = result_end.find((e) => e.name === start.name);

                    return {
                        name: start.name,
                        startValue: (parseFloat(start.value) * 100).toFixed(2),
                        endValue: (parseFloat(end?.value) * 100).toFixed(2),
                        change: ((parseFloat(end?.value) - parseFloat(start.value)) * 10000).toFixed(1)
                    };
                }
            );
            const swap_2y_start = result_start.find((item) => item.name === "SEK/IRS/3M/2Y");
            const swap_2y_end = result_end.find((item) => item.name === "SEK/IRS/3M/2Y");
            const swap_10y_start = result_start.find((item) => item.name === "SEK/IRS/3M/10Y");
            const swap_10y_end = result_end.find((item) => item.name === "SEK/IRS/3M/10Y");
            const start_value_spread = parseFloat(swap_10y_start.value) - parseFloat(swap_2y_start.value);
            const end_value_spread = parseFloat(swap_10y_end.value) - parseFloat(swap_2y_end.value);

            const resultMisc: { name: string; startValue: string | null; endValue: string | null; change: string | null }[] = [
                {
                    name: "10-2y SEK swap",
                    startValue: (start_value_spread * 100).toFixed(2),
                    endValue: (end_value_spread * 100).toFixed(2),
                    change: ((end_value_spread - start_value_spread) * 10000).toFixed(1)
                }
            ];
            setTsIrsData(result);
            setTsMiscData(resultMisc);
        }
    }, [dataIrsStart, dataIrsEnd]);

    if (
        fetchingIrsStart ||
        fetchingIrsEnd ||
        fetchingCdsStart ||
        fetchingCdsEnd ||
        fetchingNavsStart ||
        fetchingNavsEnd ||
        fetchingFxInstr ||
        fetchingFxStart ||
        fetchingFxEnd
    )
        return (
            <div>
                <p>Loading...</p>
            </div>
        );
    if (errorCdsInstr)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(errorCdsInstr, null, 2)}</pre>
            </div>
        );
    if (errorIrsStart)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(errorIrsStart, null, 2)}</pre>
            </div>
        );

    if (errorIrsEnd)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(errorIrsEnd, null, 2)}</pre>
            </div>
        );
    if (errorCdsStart)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(errorCdsStart, null, 2)}</pre>
            </div>
        );
    if (errorCdsEnd)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(errorCdsEnd, null, 2)}</pre>
            </div>
        );
    if (errorNavsStart)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(errorNavsStart, null, 2)}</pre>
            </div>
        );
    if (errorNavsEnd)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(errorNavsEnd, null, 2)}</pre>
            </div>
        );
    if (errorFxInstr)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(errorFxInstr, null, 2)}</pre>
            </div>
        );
    if (errorFxStart)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(errorFxStart, null, 2)}</pre>
            </div>
        );
    if (errorFxEnd)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(errorFxEnd, null, 2)}</pre>
            </div>
        );
    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2 items-end">
                <div className="w-full lg:w-2/12 md:w-1/12 px-2"></div>
                <div className="w-full lg:w-1/12 md:w-2/12 sm:w-6/12 px-2">
                    <DateForm className="width-fixed" defaultDateString={startDate} dateName={"startDate"}></DateForm>
                </div>
                <div className="w-full lg:w-1/12 md:w-2/12 sm:w-6/12 px-2">
                    <DateForm className="width-fixed" defaultDateString={endDate} dateName={"endDate"}></DateForm>
                </div>
            </div>
            {isWarningDateRange && (
                <div className="warningDateRange">
                    <p>Warning: end date is before start date</p>
                </div>
            )}
            <div className="flex flex-wrap -mx-2">
                <div className="w-full lg:w-6/12 md:w-6/12 sm:w-full px-2 mb-4">
                    <Grid header="IRS" data={tsIrsData} className="table-timeseries" tableClassName="table-xs" sortable>
                        <Column field="name" className="nowrap left" />
                        <Column field="startValue" title="Start value (%)" className="nowrap left" />
                        <Column field="endValue" title="End value (%)" />
                        <Column field="change" title="Change (bp)" />
                    </Grid>
                    <Grid header="CDS" data={tsCdsData} className="table-timeseries" tableClassName="table-xs" sortable>
                        <Column field="name" className="nowrap left" />
                        <Column field="startValue" title="Start value (bp)" className="nowrap left" />
                        <Column field="endValue" title="End value (bp)" />
                        <Column field="change" title="Change (bp)" />
                    </Grid>
                    {fetchingCdsInstr && !dataCdsInstr && (
                        <div>
                            <p>Loading...</p>
                        </div>
                    )}
                    <Grid header="Spreads" data={tsMiscData} className="table-timeseries" tableClassName="table-xs" sortable>
                        <Column field="name" className="nowrap left" />
                        <Column field="startValue" title="Start value (%)" className="nowrap left" />
                        <Column field="endValue" title="End value (%)" />
                        <Column field="change" title="Change (bp)" />
                    </Grid>
                    <Grid header="FX" data={tsFxData} className="table-timeseries" tableClassName="table-xs" sortable>
                        <Column field="name" className="nowrap left" />
                        <Column field="startValue" title="Start value" className="nowrap left" />
                        <Column field="endValue" title="End value" />
                        <Column field="change" title="Change (%)" />
                    </Grid>
                </div>
                {fundNavsData.length > 0 && (
                    <div className="w-full lg:w-6/12 md:w-6/12 sm:w-full px-2 mb-4">
                        <Grid header="Fund NAVs" data={fundNavsData} className="table-timeseries" tableClassName="table-xs" sortable>
                            <Column field="name" title="Name" className="nowrap left" />
                            <Column field="startValue" title="Start value" className="nowrap left" />
                            <Column field="endValue" title="End value" className="nowrap left" />
                            <Column field="change" title="Change (%)" className="nowrap left" />
                        </Grid>
                    </div>
                )}
            </div>
        </Fragment>
    );
}
